import { throttle } from '@shared/utils/throttle';

/**
 * Given a width and a sizes config, returns the matching size key.
 */
function getSizeFromWidth(width, config) {
	// Sort the config entries by their min value.
	const entries = Object.entries(config).sort((a, b) => a[1].min - b[1].min);
	for (const [size, range] of entries) {
		if (width >= range.min && width <= range.max) {
			return size;
		}
	}
	// Fallback: return the largest size if no match is found.
	return entries[entries.length - 1][0];
}

export default {
	inserted(el, binding) {
		// Expect binding.value to be an object containing:
		//   callback: function to call with the computed size.
		//   sizes: an object with size thresholds.
		//   (optional) getSize: a custom function to calculate the size.
		const { callback, sizes, getSize: getSizeFn } = binding.value || {};

		if (typeof callback !== 'function') {
			console.warn('v-measure-width requires a callback function in its binding value.');
			return;
		}
		if (typeof sizes !== 'object' || sizes === null) {
			console.warn('v-measure-width requires a sizes configuration object in its binding value.');
			return;
		}

		// Define our measurement function.
		const measure = () => {
			// Check if the element is in the document.
			if (!document.body.contains(el)) return;

			const width = el.offsetWidth;
			// If width is 0, the element might be hidden. In that case, we avoid invoking the callback.
			if (width === 0) return;

			const size = getSizeFn ? getSizeFn(width) : getSizeFromWidth(width, sizes);
			// We wrap the callback in requestAnimationFrame for smoother updates.
			window.requestAnimationFrame(() => callback(size));
		};

		// Throttle the measure function to prevent excessive calls.
		const throttledMeasure = throttle(measure, 200);

		// Use ResizeObserver to monitor dimension changes.
		if (window.ResizeObserver) {
			el.__resizeObserver__ = new ResizeObserver(() => {
				throttledMeasure();
			});
			el.__resizeObserver__.observe(el);
		} else {
			// Fallback: use window resize event if ResizeObserver isn't supported.
			window.addEventListener('resize', throttledMeasure);
			el.__vMeasureWidthHandler__ = throttledMeasure;
		}

		// Run the initial measurement.
		throttledMeasure();
	},

	unbind(el) {
		if (el.__resizeObserver__) {
			el.__resizeObserver__.disconnect();
			delete el.__resizeObserver__;
		}
		if (el.__vMeasureWidthHandler__) {
			window.removeEventListener('resize', el.__vMeasureWidthHandler__);
			delete el.__vMeasureWidthHandler__;
		}
	},
};
